/**
 * form-persistence.js
 *
 * Handles saving and loading form data from localStorage.
 */

const STORAGE_KEY = 'cr_form_draft';
const STEP_KEY = 'cr_form_step';
const TIMESTAMP_KEY = 'cr_verification_timestamp';
const VERIFICATION_EXPIRY = 10 * 60 * 1000; // 10 minutes in ms

/**
 * Save all form field values to localStorage.
 *
 * @param {Record<string, HTMLElement>} elements - Map of field name → DOM element.
 */
export function saveFormData(elements) {
    const data = {};

    Object.entries(elements).forEach(([name, el]) => {
        if (el.type === 'checkbox') {
            data[name] = el.checked;
        } else {
            data[name] = el.value;
        }
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/**
 * Get the saved form data object from localStorage.
 *
 * @returns {Object|null}
 */
export function getSavedData() {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;

    try {
        return JSON.parse(saved);
    } catch (e) {
        console.error('[Card Registration] Failed to parse saved data:', e);
        return null;
    }
}

/**
 * Load form field values from localStorage and populate the fields.
 *
 * @param {Record<string, HTMLElement>} elements - Map of field name → DOM element.
 * @param {string[]} skipFields - (Optional) List of field keys to exclude from loading.
 */
export function loadFormData(elements, skipFields = []) {
    const data = getSavedData();
    if (!data) return;

    Object.entries(data).forEach(([name, value]) => {
        const el = elements[name];
        if (!el || skipFields.includes(name)) return;

        if (el.type === 'checkbox') {
            el.checked = !!value;
        } else {
            el.value = value || '';
        }

        // Trigger a change event so other listeners (like checkbox groups) can react
        el.dispatchEvent(new Event('change', { bubbles: true }));
    });
}

/**
 * Clear the saved form data from localStorage.
 */
export function clearFormData() {
    localStorage.removeItem(STORAGE_KEY);
    clearStepPersistence();
}

/**
 * Save the current step index and a timestamp if it's the verification slide.
 *
 * @param {number} index
 */
export function saveCurrentStep(index) {
    localStorage.setItem(STEP_KEY, index.toString());
    if (index === 1) {
        localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
    } else {
        localStorage.removeItem(TIMESTAMP_KEY);
    }
}

/**
 * Load the saved step index, respecting the 10-minute expiry for verification.
 *
 * @returns {number}
 */
export function loadCurrentStep() {
    const step = localStorage.getItem(STEP_KEY);
    const timestamp = localStorage.getItem(TIMESTAMP_KEY);

    if (!step) return 0;

    const index = parseInt(step, 10);

    // If on verification slide, check if the timestamp has expired
    if (index === 1) {
        if (!timestamp) return 0;

        const now = Date.now();
        const elapsed = now - parseInt(timestamp, 10);

        if (elapsed > VERIFICATION_EXPIRY) {
            clearStepPersistence();
            return 0;
        }
    }

    return index;
}

/**
 * Clear step and timestamp persistence.
 */
export function clearStepPersistence() {
    localStorage.removeItem(STEP_KEY);
    localStorage.removeItem(TIMESTAMP_KEY);
}
